(ns ycweb.app.admin.productsync.summary
  (:require
   [cemerick.url :as url]
   [rum.core :as rum]
   [ycweb.app.admin.productsync.productsync :as productsync]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.flow :as flow]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]
   [ycweb.util.urlstate :as urlstate]))

;; Summary process
;//=====================================================================//
;; This is the little summary block at the top of step 2, which gives a
;; breakdown of what we've found for the brand to sync
(rum/defc sync-summary-view < rum/static [_ summary batch-id]
  [:div.stats-table-card.u-max-width-480
   [:table.stats-table
    [:tbody
     [:tr.stats-table__row
      [:th.stats-table__th "Brand to import:"]
      [:td.stats-table__td.u-text-right
       (:ent/display-name (:brand summary))]]
     [:tr.stats-table__row
      [:th.stats-table__th "Suppliers:"]
      [:td.stats-table__td.u-text-right
       (apply str (interpose ", " (map :ent/display-name (:suppliers summary))))]]
     [:tr.stats-table__row
      [:th.stats-table__th "Number of products:"]
      [:td.stats-table__td.u-text-right
       (str (:product/count (:stats summary)))]]
     [:tr.stats-table__row
      [:th.stats-table__th "Number of SKUs:"]
      [:td.stats-table__td.u-text-right
       (str (:sku/count (:stats summary)))]]
     [:tr.stats-table__row
      [:th.stats-table__th "Batch ID:"]
      [:td.stats-table__td.u-text-right
       (str batch-id)]]]]])

;//=====================================================================//
;; This is the table row structure <tr> that shows each individual SKUs to be synced from the
;; selected brand. If there are any errors in the data, we highlight the row and show what the
;; errors are in another row below that.

(rum/defc info-row < rum/static
  [{:keys [variation/product] :as variation}]
  [:tr
   {:class (when (:variation/errors variation) (u/bem :row-status [:is-error]))}
   [:td.u-min-width-240 (:ent/display-name product)]
   [:td.u-text-nowrap (:variation/sku-code variation)]
   [:td.u-text-nowrap (:variation/barcode variation)]
   [:td.u-text-nowrap (:product/primary-category product)]
   [:td (:variation/size variation)]
   [:td (:variation/colour variation)]
   [:td (:product/brand-type product)]
   [:td (u/currency-format (:variation/cost variation))]
   [:td (u/currency-format (:variation/retail variation))]
   [:td (u/number-format (:variation/minstock variation))]
   [:td (:variation/availability variation)]
   [:td (:variation/delivery-type variation)]
   [:td (:variation/supplier-packsize variation)]
   [:td (:variation/selling-packsize variation)]
   [:td (:variation/display-packsize variation)]
   [:td (:variation/pack-description variation)]
   [:td.u-text-center
    [:i
     {:class
      (u/bem :icon
        [(if (:variation/alcoholic variation) :check :close) :is-dark :small :inline])}]]
   [:td.u-text-center
    [:i
     {:class
      (u/bem :icon
        [(if (:variation/perishable variation) :check :close) :is-dark :small :inline])}]]
   [:td.u-text-center
    [:i
     {:class
      (u/bem :icon
        [(if (:variation/bom-product? variation) :check :close) :is-dark :small :inline])}]]
   [:td.u-text-center
    [:i
     {:class
      (u/bem :icon
        [(if (:variation/bom-component? variation) :check :close) :is-dark :small :inline])}]]
   [:td.u-text-center
    [:i
     {:class
      (u/bem :icon
        [(if (:product/madeinsa product) :check :close) :is-dark :small :inline])}]]
   [:td (:variation/expiry variation)]
   [:td  (:variation/fragile variation)]
   [:td (:variation/lockdown-level variation)]
   [:td (:variation/store-grade variation)]
   [:td.u-text-center
    (if (> (count (:variation/image-links variation)) 0)
      [:span (count (:variation/image-links variation))]
      [:i
       {:class
        (u/bem :icon
          [:close :is-dark :small :inline])}])]
   [:td
    [:a
     {:href
      (:airtable/link (first (:variation/image-links variation)))}
     [:img
      {:src
       (get-in (first (:image/image (first (:variation/image-links variation))))
         ["thumbnails" "small" "url"])}]]]])

(rum/defc error-row < rum/static
  [product]
  [:tr
   {:class (u/bem :row-status [:is-error])}
   [:td
    {:colSpan "24"}
    (into
      [:ul
       {:class (u/bem :yc-list [:small-force :flush])}]
      (map
        (fn [error]
          [:li
           (if (:link error)
             [:a {:href (:link error), :target "_blank"} (:msg error)]
             (:msg error))])
        (:variation/errors product)))]])

(rum/defc product-row < rum/static
  [product]
  (remove nil?
    [(rum/with-key
       (info-row product)
       (str (u/uuid)))
     (when (:variation/errors product)
       (rum/with-key
         (error-row product)
         (str (u/uuid))))]))

;//=====================================================================//
;; This is the third "screen" which shows the summary table into which
;; we pull the SKU row data (above) for you to check if it's all correct.
(rum/defc summary-view < rum/reactive cmn/cursors [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        summary-overview (cmn/curact process-atom [:product-sync/summary :summary])
        summary (:summary summary-overview)
        products (:products summary-overview)
        batch-id (:batch-id summary-overview)]

    [:div.admin-react-wrapper__content
     (headings/heading "Review product content to import" {:type :h1 :size :large})
     [:div..u-margin-bottom-24
      (buttons/button-web "Reload summary"
        (merge
          {:style :secondary
           :size :default}
          (urlstate/href-on-click
            #(grid/dispatch-action app-atom :product-sync.summary/get-brand-summary {}))))]

     (sync-summary-view app-atom summary batch-id)
     [:table
      {:class (u/bem :oTable [:has-shadow])}
      (into
        [:colgroup
         [:col {:style {:min-width "240px"}}]]
        (map (fn [_] [:col]) (range 20)))

      [:thead
       [:tr
        [:th.u-min-width-240 {:scope "col"} "Product Name"]
        [:th {:scope "col"} "SKU Code"]
        [:th {:scope "col"} "Barcode"]
        [:th {:scope "col"} "Primary Category"]
        [:th {:scope "col"} "Size / Feature (Feature 1)"]
        [:th {:scope "col"} "Colour (Feature 2)"]
        [:th {:scope "col"} "Brand Type"]
        [:th {:scope "col"} "Cost Price"]
        [:th {:scope "col"} "Retail Price"]
        [:th {:scope "col"} "Minimum Stock Level"]
        [:th {:scope "col"} "Availability"]
        [:th {:scope "col"} "Delivery Type"]
        [:th {:scope "col"} "Supplier Pack Size"]
        [:th {:scope "col"} "Selling Pack Size"]
        [:th {:scope "col"} "Display Pack Size"]
        [:th {:scope "col"} "Pack Description"]
        [:th.u-text-center {:scope "col"} "Alcoholic"]
        [:th.u-text-center {:scope "col"} "Perishable"]
        [:th.u-text-center {:scope "col"} "BOM Product"]
        [:th.u-text-center {:scope "col"} "Bom Component"]
        [:th.u-text-center {:scope "col"} "Made In SA"]
        [:th {:scope "col"} "Expiry Length (Days)"]
        [:th {:scope "col"} "Fragile"]
        [:th {:scope "col"} "Lockdown Level"]
        [:th {:scope "col"} "Store Grade"]
        [:th {:scope "col"} "Has Image"]
        [:th {:scope "col"} "Image"]]]
      (into
        [:tbody]
        (map
          (fn [product]
            (product-row product))
          products))]
     [:div
      (buttons/button-web "Yes, import content"
        (merge
          {:style :primary
           :size :default
           :disabled (not (empty? (filter :variation/errors products)))}
          (urlstate/href-on-click
            #(do
               (cond
                 (not (empty? (filter :variation/errors products)))
                 (toaster/open-toast app-atom :error "One or more products have errors" {})

                 (or (empty? products) (nil? products))
                 (toaster/open-toast app-atom :error "No products to upload" {})

                 :else
                 (urlstate/redirect app-atom "product-sync" "import" (cmn/screen-params app-atom)))))))]
     (toaster/loading-overlay app-atom :admin.product-sync/summary)]))

(defn get-brand-summary [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        import-type
        (or
          (:import-type (cmn/screen-params app-atom))
          (cmn/curact process-atom [:product-sync/import-type]))
        summary-overview-atom (rum/cursor-in process-atom [:product-sync/summary :summary])
        summary-progress-atom (rum/cursor-in process-atom [:product-sync/summary :progress])
        summary-progress-text-atom (rum/cursor-in process-atom [:product-sync/summary :progress-text])
        summary (cmn/curact summary-overview-atom [:summary])
        brands-atom (rum/cursor-in process-atom [:product-sync/brands])
        brands-idx (u/index-by :ent/uuid (cmn/curact brands-atom [:brands]))
        brand
        (or
          {:ent/uuid (url/url-decode (:brand-uuid (cmn/screen-params app-atom)))}
          (get brands-idx (cmn/curact brands-atom [:selected])))]

    (reset! summary-progress-atom 0)
    (reset! summary-progress-text-atom "Loading Products from Airtable: ")
    (toaster/open-toast app-atom :processing
      (productsync/reactive-message summary-progress-text-atom summary-progress-atom)
      {:closetime 200000})
    (comms/send :admin.product-sync/summary
      {:brand {:ent/uuid (:ent/uuid brand)}
       :import-type import-type})

    (when
      (not (empty? (:products summary)))
      (urlstate/redirect app-atom "product-sync" "brands"))))

(defmethod comms/receive :admin.product-sync/summary [app-atom summary-response]
  (let [process-atom (cmn/process-cursor app-atom)
        summary-overview-atom (rum/cursor-in process-atom [:product-sync/summary :summary])]
    (toaster/close-toast app-atom)
    (reset! summary-overview-atom summary-response)))

(defmethod comms/receive :admin.product-sync/summary-update-progress [app-atom {:keys [count]}]
  (let [process-atom (cmn/process-cursor app-atom)
        summary-progress-atom (rum/cursor-in process-atom [:product-sync/summary :progress])]
    (reset! summary-progress-atom count)))

(defmethod grid/action :product-sync.summary/get-brand-summary [app-atom _ _]
  (get-brand-summary app-atom))

(defmethod flow/render-screen :admin.product-sync/summary [app-atom _]
  (summary-view app-atom))

(defmethod t/transition-open :admin.product-sync/summary [app-atom _ _]
  (get-brand-summary app-atom))
